import type { JointBinding } from "./three-d-schemas";

/**
 * One default joint binding, read out of free-form JSON.
 *
 * The producer is the `ROBOTO_device` extension inside a generated glTF, which
 * is the system of record for a binding and is not under this code's control —
 * our own converter writes it, and so may a third party's. Every reader of that
 * declaration goes through this one function; a second, subtly different parser
 * is how two views of one binding come to disagree.
 *
 * Returns `undefined` rather than throwing for anything malformed. The caller's
 * loss is one joint's *declaration*; the joint itself, and the robot around it,
 * are unaffected and can still be bound by hand.
 *
 * ## Why malformed is judged strictly
 *
 * Being lenient here fails silently rather than loudly. A binding whose
 * `element` is not a whole number is **not** downgraded to a scalar binding: an
 * un-narrowed array path does not fail when it is read, it yields element 0 of
 * the array for every joint that names it — a robot whose every joint follows
 * joint 0, which looks perfectly bound. Dropping the declaration leaves the
 * joint visibly unbound instead, which someone can see and fix.
 *
 * `element` and `elementNamedBy` are mutually exclusive, so a value carrying
 * both is refused rather than resolved by precedence. Our own producer cannot
 * write that and the JSON Schema forbids it, but a third-party converter is
 * bound by neither, and there is no defensible way to pick which selector was
 * meant.
 *
 * A non-finite `scale` is refused with the rest of the binding rather than
 * defaulted to 1: "the file said something we could not honour" is not "the file
 * said 1".
 */
export function readJointBinding(value: unknown): JointBinding | undefined {
  const binding = asRecord(value);
  if (binding === undefined) {
    return undefined;
  }

  const { topic, messagePath } = binding;
  if (
    typeof topic !== "string" ||
    topic === "" ||
    typeof messagePath !== "string" ||
    messagePath === ""
  ) {
    return undefined;
  }

  const hasElement = binding.element !== undefined && binding.element !== null;
  const hasNamedBy =
    binding.elementNamedBy !== undefined && binding.elementNamedBy !== null;
  if (hasElement && hasNamedBy) {
    return undefined;
  }

  let element: number | undefined;
  if (hasElement) {
    if (
      typeof binding.element !== "number" ||
      !Number.isInteger(binding.element) ||
      binding.element < 0
    ) {
      return undefined;
    }
    element = binding.element;
  }

  let elementNamedBy: JointBinding["elementNamedBy"];
  if (hasNamedBy) {
    const namedBy = asRecord(binding.elementNamedBy);
    const path = namedBy?.path;
    const key = namedBy?.key;
    if (typeof path !== "string" || path === "") {
      return undefined;
    }
    if (typeof key !== "string" || key === "") {
      return undefined;
    }
    elementNamedBy = { path, key };
  }

  const scale = binding.scale;
  if (scale !== undefined && scale !== null) {
    if (typeof scale !== "number" || !Number.isFinite(scale)) {
      return undefined;
    }
  }

  return {
    topic,
    messagePath,
    ...(element !== undefined ? { element } : {}),
    ...(elementNamedBy !== undefined ? { elementNamedBy } : {}),
    ...(typeof scale === "number" ? { scale } : {}),
  };
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
